import { useCallback, useEffect, useState } from 'react';
import { api } from '../../shell/bridge';
import { Pressable } from '../../ui/Pressable';
import { partnerLabel } from '../../lib/partnerName';
import './dates.css';

/**
 * Us → Dates → ⚙️ preferences & consent (PRD §6.4, card sXseXczawWR1).
 *
 * Everything PRD §6.4 asks for, on one panel: cadence slider + suggest-by
 * day, timing chips, in↔out ratio, budget band, energy, constraint tags, the
 * three-level location consent with the reassurance copy, and the derived
 * mode banner (Together / Long-distance / Unspecified) with its "how we
 * know" line.
 *
 * Two privacy rules are visible in the UI and enforced server-side: the
 * partner's row is shown as "what they've said" minus any location, and a
 * consent other than precise deletes the stored coordinates — turning
 * location Off leaves nothing behind to serve. The intersection line is the
 * only thing the concierge scores against, so the panel states plainly when
 * one partner has not saved yet.
 */

interface DatePrefsMember {
  memberId: string;
  cadence: number;
  suggestByDay: number;
  timing: string[];
  modeRatio: number;
  budget: string;
  energy: string;
  constraints: string[];
  factorAttitudes: Record<string, 'desirable' | 'neutral' | 'undesirable'>;
  locationConsent: 'precise' | 'city' | 'off';
  locationLabel: string | null;
  lat: number | null;
  lng: number | null;
  updatedAt: number;
}

interface DatePrefsView {
  mine: DatePrefsMember;
  partner: DatePrefsMember | null;
  intersection: {
    cadence: number;
    suggestByDay: number | null;
    timing: string[];
    modeRatio: number | null;
    budget: string;
    energy: string | null;
    constraints: string[];
  } | null;
  derivedMode: 'together' | 'long_distance' | 'unspecified';
  derivedModeReason: string;
}

const TIMINGS: { value: string; label: string }[] = [
  { value: 'weekday_eve', label: 'Weekday eve' },
  { value: 'weekend_day', label: 'Weekend day' },
  { value: 'weekend_eve', label: 'Weekend eve' },
];
const BUDGETS = [
  { value: 'free', label: 'Free' },
  { value: '$', label: '$' },
  { value: '$$', label: '$$' },
  { value: '$$$', label: '$$$' },
];
const ENERGIES = [
  { value: 'low', label: 'Cozy' },
  { value: 'med', label: 'Easy' },
  { value: 'high', label: 'Big' },
];
/** Card GMat5z0Zxutf: the decision factors, each with a Desirable / Neutral /
 *  Undesirable attitude. These ARE the constraint set — Undesirable lands back
 *  in the couple's rule-out list so the scorer's contract never changes. */
const FACTORS: { value: string; label: string }[] = [
  { value: 'near_transit', label: 'Near transit' },
  { value: 'kid_friendly', label: 'Kid friendly' },
  { value: 'vego', label: 'Vego / veggie options' },
  { value: 'no_late_nights', label: 'No late nights' },
  { value: 'outdoorsy', label: 'Outdoorsy' },
];
const ATTITUDE_LABEL: Record<string, string> = {
  desirable: 'Desirable',
  neutral: 'Neutral',
  undesirable: 'Undesirable',
};
const ATTITUDE_HINT: Record<string, string> = {
  desirable: 'We lean toward this',
  neutral: "Don't care either way",
  undesirable: 'Rule it out',
};
const CONSENTS: { value: string; label: string; blurb: string }[] = [
  { value: 'precise', label: 'Precise', blurb: 'Best matches, exact travel times' },
  { value: 'city', label: 'City', blurb: 'City only — no street level' },
  { value: 'off', label: 'Off', blurb: 'Never used, deleted if saved' },
];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MODE_LABEL: Record<string, string> = {
  together: 'Together',
  long_distance: 'Long-distance',
  unspecified: 'Unspecified',
};
/** Card DfhelObKj9iP — paired state names her; module-level so the copy has one home. */
const reassurance = () => `Shapes suggestions only. ${partnerLabel()} never sees where you are.`;

export function DatesPrefsPanel({ partnerName, onDone }: { partnerName: string; onDone: () => void }) {
  const [view, setView] = useState<DatePrefsView | null>(null);
  const [draft, setDraft] = useState<DatePrefsMember | null>(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [geoNote, setGeoNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api<DatePrefsView>('/api/dates/prefs');
      setView(data);
      setDraft(data.mine);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (failed) return <p className="dates-empty" id="dates-prefs">Couldn't load your date preferences.</p>;
  if (!view || !draft) return <p className="dates-empty" id="dates-prefs">Loading preferences…</p>;

  const patch = (p: Partial<DatePrefsMember>) => {
    setDraft({ ...draft, ...p });
    setSaved(false);
  };
  const toggle = (key: 'timing' | 'constraints', value: string) => {
    const list = draft[key];
    patch({ [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] } as Partial<DatePrefsMember>);
  };
  const setFactor = (factor: string, attitude: 'desirable' | 'neutral' | 'undesirable') => {
    patch({ factorAttitudes: { ...(draft.factorAttitudes ?? {}), [factor]: attitude } } as Partial<DatePrefsMember>);
  };

  const save = async () => {
    setSaving(true);
    try {
      const next = await api<DatePrefsView>('/api/dates/prefs', {
        method: 'PUT',
        body: {
          cadence: draft.cadence,
          suggestByDay: draft.suggestByDay,
          timing: draft.timing,
          modeRatio: draft.modeRatio,
          budget: draft.budget,
          energy: draft.energy,
          constraints: draft.constraints,
          factorAttitudes: draft.factorAttitudes ?? {},
          locationConsent: draft.locationConsent,
          locationLabel: draft.locationLabel,
          lat: draft.lat,
          lng: draft.lng,
        },
      });
      setView(next);
      setDraft(next.mine);
      setSaved(true);
      setGeoNote(null);
    } catch {
      setGeoNote("Couldn't save — try again.");
    } finally {
      setSaving(false);
    }
  };

  /** Precise consent is the only level that asks the browser for a fix. */
  const useMyLocation = () => {
    if (!('geolocation' in navigator)) {
      setGeoNote('This browser has no location to give.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        patch({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoNote('Location saved with this preference.');
      },
      () => setGeoNote("Couldn't get your location — city level works fine."),
      { timeout: 8000 },
    );
  };

  const shared = view.intersection;

  return (
    <div className="dates-prefs" id="dates-prefs">
      <div className="dates-prefs-head">
        <h3 className="dates-headline">Date preferences</h3>
        <Pressable className="pear-btn pear-btn-ghost" id="dates-prefs-close" onClick={onDone}>
          Done
        </Pressable>
      </div>

      {/* PRD §6.4: the derived banner, read-only, with the reason spelled out. */}
      <div className="dates-prefs-mode">
        <span className="dates-prefs-mode-label" id="dates-prefs-mode" data-mode={view.derivedMode}>
          {MODE_LABEL[view.derivedMode]}
        </span>
        <span className="dates-prefs-mode-why" id="dates-prefs-mode-why">{view.derivedModeReason}</span>
      </div>

      <section className="dates-prefs-group">
        <label className="dates-prefs-label" htmlFor="dates-prefs-cadence">
          How often — <strong id="dates-prefs-cadence-value">{draft.cadence} a month</strong>
        </label>
        <input
          id="dates-prefs-cadence"
          className="dates-prefs-range"
          type="range"
          min={1}
          max={4}
          step={1}
          value={draft.cadence}
          onChange={(e) => patch({ cadence: Number(e.target.value) })}
        />
        <label className="dates-prefs-label" htmlFor="dates-prefs-day">Suggest by</label>
        <select
          id="dates-prefs-day"
          className="dates-input dates-prefs-select"
          value={draft.suggestByDay}
          onChange={(e) => patch({ suggestByDay: Number(e.target.value) })}
        >
          {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
        </select>
      </section>

      <section className="dates-prefs-group">
        <span className="dates-prefs-label">When suits</span>
        <div className="date-chips" id="dates-prefs-timing" role="group" aria-label="When suits">
          {TIMINGS.map((t) => (
            <button
              key={t.value}
              type="button"
              className={`date-chip-opt pear-tap-capsule${draft.timing.includes(t.value) ? ' is-on' : ''}`}
              data-timing={t.value}
              aria-pressed={draft.timing.includes(t.value)}
              onClick={() => toggle('timing', t.value)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      <section className="dates-prefs-group">
        <label className="dates-prefs-label" htmlFor="dates-prefs-ratio">
          In ↔ out — <strong id="dates-prefs-ratio-value">{draft.modeRatio <= 20 ? 'mostly in' : draft.modeRatio >= 80 ? 'mostly out' : 'a bit of both'}</strong>
        </label>
        <input
          id="dates-prefs-ratio"
          className="dates-prefs-range"
          type="range"
          min={0}
          max={100}
          step={10}
          value={draft.modeRatio}
          onChange={(e) => patch({ modeRatio: Number(e.target.value) })}
        />
      </section>

      <section className="dates-prefs-group">
        <span className="dates-prefs-label">Budget</span>
        <div className="date-chips" id="dates-prefs-budget" role="group" aria-label="Budget">
          {BUDGETS.map((b) => (
            <button
              key={b.value}
              type="button"
              className={`date-chip-opt pear-tap-capsule${draft.budget === b.value ? ' is-on' : ''}`}
              data-budget={b.value}
              aria-pressed={draft.budget === b.value}
              onClick={() => patch({ budget: b.value })}
            >
              {b.label}
            </button>
          ))}
        </div>
        <span className="dates-prefs-label">Energy</span>
        <div className="date-chips" id="dates-prefs-energy" role="group" aria-label="Energy">
          {ENERGIES.map((e) => (
            <button
              key={e.value}
              type="button"
              className={`date-chip-opt pear-tap-capsule${draft.energy === e.value ? ' is-on' : ''}`}
              data-energy={e.value}
              aria-pressed={draft.energy === e.value}
              onClick={() => patch({ energy: e.value })}
            >
              {e.label}
            </button>
          ))}
        </div>
        <span className="dates-prefs-label" id="dates-prefs-factors-title">Day-to-day factors</span>
        <p className="dates-prefs-blurb" id="dates-prefs-factors-hint">
          For each one, how does it sit? Undesirable is a hard rule-out — either of you can rule it out for both of you. Desirable sways what the concierge goes looking for.
        </p>
        <div className="date-factors" id="dates-prefs-factors" role="group" aria-label="Day-to-day factor attitudes">
          {FACTORS.map((f) => {
            const att = (draft.factorAttitudes ?? {})[f.value] ?? 'neutral';
            return (
              <div className="date-factor-row" key={f.value} data-factor={f.value}>
                <span className="date-factor-label">{f.label}</span>
                <div className="date-factor-opts" role="radiogroup" aria-label={f.label}>
                  {(['desirable', 'neutral', 'undesirable'] as const).map((a) => (
                    <button
                      key={a}
                      type="button"
                      className={`date-factor-opt${att === a ? ' is-on' : ''}`}
                      data-attitude={a}
                      data-factor-opt={f.value}
                      aria-pressed={att === a}
                      title={ATTITUDE_HINT[a]}
                      onClick={() => setFactor(f.value, a)}
                    >
                      {ATTITUDE_LABEL[a]}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="dates-prefs-group">
        <span className="dates-prefs-label">Location</span>
        <div className="date-chips" id="dates-prefs-location" role="group" aria-label="Location">
          {CONSENTS.map((c) => (
            <button
              key={c.value}
              type="button"
              className={`date-chip-opt pear-tap-capsule${draft.locationConsent === c.value ? ' is-on' : ''}`}
              data-consent={c.value}
              aria-pressed={draft.locationConsent === c.value}
              onClick={() => patch({ locationConsent: c.value as DatePrefsMember['locationConsent'] })}
            >
              {c.label}
            </button>
          ))}
        </div>
        <p className="dates-prefs-blurb" id="dates-prefs-location-blurb">
          {CONSENTS.find((c) => c.value === draft.locationConsent)?.blurb} — {reassurance()}
        </p>
        {draft.locationConsent === 'city' && (
          <input
            id="dates-prefs-city"
            className="dates-input"
            placeholder="Your city (e.g. Melbourne)"
            value={draft.locationLabel ?? ''}
            onChange={(e) => patch({ locationLabel: e.target.value })}
          />
        )}
        {draft.locationConsent === 'precise' && (
          <div className="dates-prefs-geo">
            <Pressable className="pear-btn pear-btn-ghost" id="dates-prefs-geo" onClick={useMyLocation}>
              {draft.lat != null ? 'Update my location' : 'Use my location'}
            </Pressable>
            {draft.lat != null && (
              <span className="dates-prefs-blurb" id="dates-prefs-geo-set">
                Saved: {draft.lat.toFixed(3)}, {draft.lng?.toFixed(3)}
              </span>
            )}
          </div>
        )}
        {geoNote && <p className="dates-prefs-blurb" id="dates-prefs-geo-note">{geoNote}</p>}
      </section>

      <section className="dates-prefs-group">
        <span className="dates-prefs-label">What you've both said</span>
        <p className="dates-prefs-blurb" id="dates-prefs-shared">
          {shared
            ? `${shared.cadence} a month, up to ${shared.budget}${shared.timing.length ? `, when it's ${shared.timing.map((t) => TIMINGS.find((x) => x.value === t)?.label ?? t).join(' or ')}` : ''} — the concierge only scores this overlap.`
            : `${partnerName} hasn't saved their preferences yet, so nothing of yours is being scored until they do.`}
        </p>
      </section>

      <div className="dates-prefs-actions">
        <Pressable className="pear-btn pear-btn-mustard" id="dates-prefs-save" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save preferences'}
        </Pressable>
        {saved && <span className="dates-prefs-blurb" id="dates-prefs-saved">Saved — this is what the concierge will use.</span>}
      </div>
    </div>
  );
}
